import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MainLayout } from './components/layout';
import { Login, ForgotPassword, ResetPassword, Dashboard, PurchaseHistory, AccountStatement, Settings, AdminPanel, EjecutivoPanel } from './pages';
import { Loader } from './components/common';
import './styles/globals.css';

// Componente para rutas protegidas (usuarios normales)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <Loader fullScreen text="Verificando sesión..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si es admin, redirigir al panel de admin (excepto configuración que es compartida)
  if (user?.role === 'admin' && window.location.pathname !== '/configuracion') {
    return <Navigate to="/admin" replace />;
  }

  // Si es ejecutivo, redirigir al panel de ejecutivo (excepto configuración)
  if (user?.role === 'ejecutivo' && window.location.pathname !== '/configuracion') {
    return <Navigate to="/ejecutivo" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas solo de ejecutivos
const EjecutivoRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <Loader fullScreen text="Verificando sesión..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'ejecutivo') {
    // Si es admin, redirigir a admin
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    // Si es usuario normal, redirigir a dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas solo de admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <Loader fullScreen text="Verificando sesión..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas públicas (redirige si ya está autenticado)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <Loader fullScreen text="Cargando..." />;
  }

  if (isAuthenticated) {
    // Redirigir según el rol
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'ejecutivo') {
      return <Navigate to="/ejecutivo" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Componente principal de la aplicación
const AppContent: React.FC = () => {
  return (
    <Routes>
      {/* Rutas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Rutas protegidas con layout */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/historial" element={<PurchaseHistory />} />
        <Route path="/estado-cuenta" element={<AccountStatement />} />
        <Route path="/configuracion" element={<Settings />} />
      </Route>

      {/* Ruta de administración */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <MainLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminPanel />} />
      </Route>

      {/* Ruta de ejecutivos */}
      <Route
        path="/ejecutivo"
        element={
          <EjecutivoRoute>
            <MainLayout />
          </EjecutivoRoute>
        }
      >
        <Route index element={<EjecutivoPanel />} />
        <Route path="configuracion" element={<Settings />} />
      </Route>

      {/* Redirección por defecto */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// App con providers
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
